import type { TSource } from './api';

export type TSourceStateFilter = TSource['state'];
export type TSourceAuthFilter = 'checked' | 'unchecked';

export interface ISourceFilters {
  state?: TSourceStateFilter;
  auth?: TSourceAuthFilter;
  hasProblems?: boolean;
  q?: string;
  cursor?: string;
}

const STATES: readonly string[] = ['active', 'disabled'];
const AUTHS: readonly string[] = ['checked', 'unchecked'];

export const parseSourceFilters = (search: Record<string, unknown>): ISourceFilters => {
  const text = (key: string) =>
    typeof search[key] === 'string' && search[key] !== '' ? (search[key] as string) : undefined;
  const state = text('state');
  const auth = text('auth');
  return {
    state: state && STATES.includes(state) ? (state as TSourceStateFilter) : undefined,
    auth: auth && AUTHS.includes(auth) ? (auth as TSourceAuthFilter) : undefined,
    hasProblems: search.hasProblems === true || search.hasProblems === 'true' ? true : undefined,
    q: text('q'),
    cursor: text('cursor'),
  };
};

// `true`, not `'true'`: the router JSON-encodes a string that reads as JSON.
export const sourceFiltersToSearch = (
  filters: ISourceFilters,
): Record<string, string | boolean> => {
  const out: Record<string, string | boolean> = {};
  if (filters.state) out.state = filters.state;
  if (filters.auth) out.auth = filters.auth;
  if (filters.hasProblems) out.hasProblems = true;
  if (filters.q) out.q = filters.q;
  if (filters.cursor) out.cursor = filters.cursor;
  return out;
};

/** The filters the server does not take, applied to the page in hand. */
export const keepsSource = (
  source: TSource,
  filters: ISourceFilters,
  hasProblems: (source: TSource) => boolean,
): boolean =>
  (!filters.state || source.state === filters.state) &&
  (!filters.auth || source.unauthenticated === (filters.auth === 'unchecked')) &&
  (!filters.hasProblems || hasProblems(source));
